import { CategoryMappingApi } from '@/utilities/api/CategoryMappingApi';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, FullscreenDialog, Text } from '@/components/DesignSystem';
import { Stack, useTheme } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { QueryKeys } from '@/constants/QueryKeys';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { CategoryMappingEndpoint } from '@api/endpoints';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { doesMappingMatchNames } from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/helpers';
import {
  AddIncentivesStep,
  IncentiveCategory,
} from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/AddIncentivesStep';
import { ApiCategoryMapping } from '@api/interfaces/ApiCategoryMapping';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import {
  MapCategoriesStep,
} from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/MapCategoriesStep';
import { ApiCategory } from '@api/interfaces';
import { RewardsProgramType } from '@shared/enums';

export type CategoryToSync = {
  erpCategoryIdsToMap: string[],
  name: string,
  subcategories?: {
    erpCategoryIdsToMap: string[],
    name: string,
  }[],
};

export type ErpToIncentive = {
  categoryName: string,
  erpCategoryId: string,
  subcategoryName?: string,
};

interface CategoryMappingModalProps {
  categoryMapping?: ApiCategoryMapping,
  isDuplicating?: boolean,
  isEditing?: boolean,
  existingCategoryMappings: ApiCategoryMapping[],
  programType: RewardsProgramType,
  programs?: ApiRewardsProgram[],
  retailerId: string,
  onClose: () => void,
}

export const CategoryMappingModal = ({
  categoryMapping,
  isDuplicating = false,
  isEditing = false,
  existingCategoryMappings,
  programType,
  programs,
  retailerId,
  onClose,
}: CategoryMappingModalProps) => {
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(0);
  const initialCategories = useMemo(() => (
    categoryMapping?.categories
      ?.filter(({ parentId }) => !parentId)
      .map(({ name, masterSubcategories }) => ({
        name,
        subcategories:
          masterSubcategories?.map((subcategory) => subcategory.name) ?? [],
      })) ?? []
  ), [categoryMapping]);
  const [incentiveCategories, setIncentiveCategories] = useState<IncentiveCategory[]>(
    initialCategories,
  );
  const [mappings, setMappings] = useState<ErpToIncentive[]>([]);
  const [name, setName] = useState<string>(categoryMapping?.name ?? '');

  const { data: erpCategories } = useQuery(
    [QueryKeys.GET_RETAILER_ERP_CATEGORIES, retailerId],
    async () => RetailerApi.listErpCategories(retailerId),
  );

  const { data: dbCategories } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORIES, retailerId],
    async () => RetailerApi.listCategories(retailerId),
  );

  const initialMappings = useMemo(() => (
    (categoryMapping?.id && !!erpCategories)
      ? erpCategories
        .filter(({ cats }) => !!cats?.length)
        .map(({ id, cats }) => {
          const mappedCategory: ApiCategory | undefined = cats!.find(({ mappingId }) => (
            mappingId === categoryMapping.id
          ));
          if (!mappedCategory) {
            return;
          }
          if (mappedCategory.parent) {
            return {
              categoryName: mappedCategory.parent.name,
              erpCategoryId: id,
              subcategoryName: mappedCategory!.name,
            };
          } else {
            return {
              categoryName: mappedCategory!.name,
              erpCategoryId: id,
            };
          }
        }).filter(Boolean)
      : []
  ) as ErpToIncentive[], [erpCategories, categoryMapping?.id]);

  useEffect(() => setMappings(initialMappings), [initialMappings]);

  const isNameDuplicate = useMemo(() => (
    !!existingCategoryMappings.find((cm) => (
      cm.name === name
      && (
        cm.id !== categoryMapping?.id
        || isDuplicating
      )
    ))
  ), [categoryMapping, existingCategoryMappings, isDuplicating, name]);

  const { mutateAsync: syncMappings, isLoading: isSyncing } = useMutation(
    (body: CategoryMappingEndpoint.Sync.Request) => (
      CategoryMappingApi.sync(retailerId, body)
    ),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKeys.GET_RETAILER_CATEGORIES]);
        void queryClient.invalidateQueries([QueryKeys.GET_RETAILER_ERP_CATEGORIES]);
        void queryClient.invalidateQueries([QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS]);
        // An energy program's readiness depends on its mapping's ERP categories.
        void queryClient.invalidateQueries([QueryKeys.GET_ENERGY_PROGRAM]);
        onClose();
        const isUpdate = isEditing && !isDuplicating;
        openSnackbar(isUpdate ? 'Category mapping updated' : 'Category mapping created');
      },
      onError: (error: { message: string }) => {
        const isUpdate = isEditing && !isDuplicating;
        openSnackbar(
          error.message
          || (isUpdate ? 'Error updating category mapping' : 'Error creating category mapping'),
        );
      },
    },
  );

  useEffect(() => {
    if (mappings.length) {
      const newMappings = mappings.filter((mapping) => {
        const category = incentiveCategories.find(({ name }) => name === mapping.categoryName);
        if (!category) {
          return false;
        }
        if (mapping.subcategoryName) {
          return category.subcategories.includes(mapping.subcategoryName);
        }
        return true;
      });
      setMappings(newMappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentiveCategories]);

  const hasNameChanged = categoryMapping?.name !== name;

  const handleSync = async () => {
    if (!erpCategories || !name) {
      return;
    }
    const categoriesToSync: CategoryToSync[] = incentiveCategories.map((category) => ({
      name: category.name,
      subcategories: category.subcategories.map((subcategoryName) => ({
        name: subcategoryName,
        erpCategoryIdsToMap: [...new Set(mappings
          .filter((mapping) => (
            doesMappingMatchNames(mapping, category.name, subcategoryName)
          ))
          .flatMap((mapping) => {
            const mappedErpCategory = erpCategories.find(({ id }) => id === mapping.erpCategoryId)!;
            return erpCategories.filter((erpCategory) => (
              erpCategory.name === mappedErpCategory.name
              && erpCategory.parentId === mappedErpCategory.parentId
            )).map(({ id }) => id);
          }))],
      })),
      erpCategoryIdsToMap: [...new Set(mappings
        .filter((mapping) => mapping.categoryName === category.name && !mapping.subcategoryName)
        .flatMap((mapping) => {
          const mappedErpCategory = erpCategories.find(({ id }) => id === mapping.erpCategoryId)!;
          return erpCategories.filter((erpCategory) => (
            erpCategory.name === mappedErpCategory.name
            && erpCategory.parentId === mappedErpCategory.parentId
          )).map(({ id }) => id);
        }))],
    }));
    await syncMappings({
      categories: categoriesToSync,
      programType,
      ...(hasNameChanged ? { name } : {}),
      ...((!isDuplicating && !!categoryMapping?.id) ? { id: categoryMapping.id } : {}),
    });
    onClose();
  };

  const hasChanged = useMemo(() => {
    const areSetsEqual = (a: Set<string>, b: Set<string>) => (
      a.size === b.size && [...a].every(value => b.has(value))
    );
    if (hasNameChanged) {
      return true;
    }
    const initialNames = new Set(initialCategories.map(({ name }) => name));
    const incentiveNames = new Set(incentiveCategories.map(({ name }) => name));
    if (!areSetsEqual(initialNames, incentiveNames)) {
      return true;
    }
    const hasSubcategoryChanged = initialCategories.some(({ name, subcategories }) => {
      const initialSubcategoryNames = new Set(subcategories);
      const incentiveSubcategoryNames = new Set(incentiveCategories
        .find((category) => category.name === name)?.subcategories ?? []);
      return !areSetsEqual(initialSubcategoryNames, incentiveSubcategoryNames);
    });
    if (hasSubcategoryChanged) {
      return true;
    }
    const haveMappingsChanged = mappings.some((mapping) => {
      const initialMapping = initialMappings.find((initialMapping) => (
        initialMapping.categoryName === mapping.categoryName
        && initialMapping.subcategoryName === mapping.subcategoryName
        && initialMapping.erpCategoryId === mapping.erpCategoryId
      ));
      return !initialMapping;
    });
    if (haveMappingsChanged || mappings.length !== initialMappings.length) {
      return true;
    }
    return false;
  }, [hasNameChanged, incentiveCategories, initialCategories, initialMappings, mappings]);

  const isStep1Valid = (
    !!erpCategories && !!incentiveCategories.length && !!name && !isNameDuplicate
  );
  const assignedProgram = programs?.find((p) => (
    p.id === categoryMapping?.programId
  ));

  return (
    <FullscreenDialog
      dialogActions={
        <Stack
          alignItems="center"
          borderTop={`1px solid ${theme.palette.outline}`}
          height="80px"
          px="72px"
          width="100%"
        >
          <Stack
            alignItems="center"
            direction="row"
            height="100%"
            justifyContent="space-between"
            maxWidth="800px"
            width="100%"
          >
            <Button
              onClick={onClose}
              testID="category-mapping-modal-close-button"
              variant="outlined"
            >
              {isEditing ? 'Cancel' : 'Close'}
            </Button>
            <Stack direction="row" justifyContent="space-between">
              {currentStep === 0 && (
                <Button
                  disabled={!isStep1Valid}
                  endIcon={<ArrowForward />}
                  onClick={() => setCurrentStep(currentStep + 1)}
                  testID="category-mapping-modal-cancel-button"
                  variant="contained"
                >
                  Next
                </Button>
              )}
              {currentStep === 1 && (
                <Stack direction="row" gap="16px">
                  <Button
                    onClick={() => setCurrentStep(currentStep - 1)}
                    startIcon={<ArrowBack />}
                    testID="category-mapping-modal-back-button"
                    variant="text"
                  >
                    Back
                  </Button>
                  {isEditing && (
                    <Button
                      disabled={!hasChanged || isNameDuplicate}
                      loading={isSyncing}
                      onClick={handleSync}
                      testID="category-mapping-modal-save-button"
                      variant="contained"
                    >
                      Save
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      }
      hideHeader
      hideTitleBorder
      open
      testID="category-mapping-modal"
    >
      <Stack alignItems="center" justifyContent="center" py="12px" width="100%">
        <Text category="overline">CATEGORY MAPPING</Text>
      </Stack>
      <Stack alignItems="center">
        {currentStep === 0 && (
          <AddIncentivesStep
            assignedProgram={assignedProgram}
            categories={incentiveCategories}
            hasPointsEngineBeenRun={assignedProgram?.arePointsProcessed}
            isEditing={isEditing}
            isNameDuplicate={isNameDuplicate}
            name={name}
            onChangeCategories={setIncentiveCategories}
            onChangeName={setName}
          />
        )}
        {currentStep === 1 && isStep1Valid && !!incentiveCategories && (
          <MapCategoriesStep
            categories={incentiveCategories}
            dbCategories={dbCategories ?? []}
            erpCategories={erpCategories}
            hasPointsEngineBeenRun={assignedProgram?.arePointsProcessed}
            isEditing={isEditing}
            mappings={mappings}
            onChange={setMappings}
            program={assignedProgram}
          />
        )}
      </Stack>
    </FullscreenDialog>
  );
};
